import React, { ReactNode } from 'react'
import { Box, Button, Typography } from '@mui/joy'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'

interface ErrorBoundaryProps {
    children: ReactNode
    fallback?: (error: Error, retry: () => void) => ReactNode
}

interface ErrorBoundaryState {
    hasError: boolean
    error: Error | null
}

/**
 * Error Boundary Component
 * Catches JavaScript errors anywhere in the child component tree
 * Displays an error UI and allows retry functionality
 *
 * Usage:
 * <ErrorBoundary>
 *   <SomeComponent />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props)
        this.state = {
            hasError: false,
            error: null,
        }
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return {
            hasError: true,
            error,
        }
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        console.error('[PrivateLoads ErrorBoundary]', error.message, errorInfo.componentStack)
    }

    handleRetry = () => {
        this.setState({
            hasError: false,
            error: null,
        })
    }

    render() {
        if (this.state.hasError && this.state.error) {
            return this.props.fallback ? (
                this.props.fallback(this.state.error, this.handleRetry)
            ) : (
                <DefaultErrorFallback error={this.state.error} onRetry={this.handleRetry} />
            )
        }

        return this.props.children
    }
}

interface DefaultErrorFallbackProps {
    error: Error
    onRetry: () => void
}

/**
 * Default Error Fallback UI
 * Displayed when an error is caught and no custom fallback is provided
 */
const DefaultErrorFallback: React.FC<DefaultErrorFallbackProps> = ({ error, onRetry }) => (
    <Box
        sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            p: 3,
            minHeight: '300px',
            height: '100%',
            // Theme-aware surfaces: with a fixed light gray, Joy's Typography (which follows
            // the dark palette) rendered light text on a light panel — an invisible error message.
            bgcolor: 'var(--lh-surface-secondary)',
            borderRadius: '8px',
        }}
    >
        <ErrorOutlineIcon sx={{ fontSize: '3rem', color: 'error.main' }} />
        <Typography level="h3">Something went wrong</Typography>
        <Typography level="body-md" sx={{ textAlign: 'center', maxWidth: '400px' }}>
            An unexpected error occurred. Please try again.
        </Typography>
        {process.env.NODE_ENV === 'development' && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'var(--lh-surface)', borderRadius: '4px', maxWidth: '500px', overflow: 'auto' }}>
                <Typography level="body-sm" component="pre" sx={{ color: 'error.main', fontSize: '0.75rem' }}>
                    {error.message}
                </Typography>
            </Box>
        )}
        <Button onClick={onRetry} color="primary" variant="solid" sx={{ mt: 2 }}>
            Try Again
        </Button>
    </Box>
)
